import React, { useState } from 'react';
import { DialogTitle, DialogContent, Dialog, Button, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { validateSUMSCriteria } from '@/helpers/administrationUtils';
import { formYESNO } from '@/helpers/constants';
import useToastContext from '@/hooks/useToastContext';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import administrationServices from '@/services/administrationServices';
import { SUMSCriteriaINT } from '@/types';
import Dropdown from '../UI/Dropdown/Dropdown';
import InputText from '../UI/InputText/InputText';
import styles from './DialogAddSUMSCriteria.module.scss';

interface DialogAddSUMSCriteriaProps {
  close: () => void;
  title: string;
  reloadSUMSCriteria: () => void,
  allCriterias: SUMSCriteriaINT[]
}

const requiredFields = ['efaNumber', 'sumsRelevant'];

const INITIAL_FORM_VALUE: SUMSCriteriaINT = {
  efaNumber: '',
  description: '',
  sumsRelevant: '',
  definition: '',
  comment: '',
};

const DialogAddSUMSCriteria: React.FC<DialogAddSUMSCriteriaProps> = (props) => {
  const { close, title, reloadSUMSCriteria, allCriterias } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();
  const [fieldsCompleted, setFieldsCompleted] = useState(false);
  const [criteria, setCriteria] = useState(INITIAL_FORM_VALUE);

  const handleCriteriaChange = (inputName: string, newValue: string) => {
    const newState = { ...criteria, [inputName]: newValue };
    const fieldsOk = requiredFields.reduce((fieldOk, fieldToCheck) => (newState[fieldToCheck]?.length > 0 ? fieldOk + 1 : fieldOk), 0);
    setFieldsCompleted(fieldsOk === requiredFields.length);
    setCriteria(newState);
  };

  const saveCriteria = () => {
    if (validateSUMSCriteria(criteria, allCriterias, addToast, t, false)) {
      setLoading(true);
      administrationServices.createSUMSCriteria(criteria)
        .then(reloadSUMSCriteria)
        .catch((error) => addToast(error.message, 'ERROR'))
        .finally(() => setLoading(false));
    }
  };

  return (
    <Dialog id="form-dialog-add-sums-criteria" open maxWidth="md" onClose={close}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={styles.DialogContent}>
        <div className={styles.DialogProductGroup}>
          <div className={styles.PageContent}>
            <div className={styles.Fields}>
              <InputText
                id="create-criteria-efanumber"
                value={criteria.efaNumber}
                isMandatory
                label={t('administration.sumsCriteria.efaNumber')}
                handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleCriteriaChange('efaNumber', ev.target.value)}
                maxLength={3}
              />
              <Dropdown
                id="create-criteria-sumsrelevant"
                label={t('administration.sumsCriteria.sumsRelevant')}
                options={formYESNO}
                value={criteria.sumsRelevant}
                isMandatory
                handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleCriteriaChange('sumsRelevant', ev.target.value)}
              />
              <InputText
                id="create-criteria-description"
                value={criteria.description}
                label={t('administration.sumsCriteria.description')}
                handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleCriteriaChange('description', ev.target.value)}
                multiline
                rows={3}
                maxLength={100}
              />
              <InputText
                id="create-criteria-definition"
                value={criteria.definition}
                label={t('administration.sumsCriteria.definition')}
                handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleCriteriaChange('definition', ev.target.value)}
                multiline
                rows={3}
                maxLength={250}
              />
            </div>
            <div className={styles.FullField}>
              <InputText
                id="create-criteria-comment"
                value={criteria.comment}
                label={t('administration.sumsCriteria.comment')}
                handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleCriteriaChange('comment', ev.target.value)}
                multiline
                maxLength={250}
                rows={2}
                fullwidth
              />
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        {!fieldsCompleted && (<div className={styles.FieldsMissing}>{t('administration.sumsCriteria.fieldsMissing')}</div>)}
        <Button id="button-create-criteria" variant="contained" color="primary" onClick={saveCriteria} disabled={!fieldsCompleted}>
          {t('dialog.save')}
        </Button>
        <Button id="button-close-dialog-criteria" color="primary" onClick={close}>
          {t('dialog.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogAddSUMSCriteria;
